import React, { useState, useEffect } from 'react';
import '../CSS/TabbedInfoCard.css';
import { ref, get } from 'firebase/database';
import { db } from '../firebaseConfig';

const TabbedInfoCard = ({ isHackerTheme }) => {
  const [activeTab, setActiveTab] = useState('education');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [certificates, setCertificates] = useState([]);
  const [certificatesLoading, setCertificatesLoading] = useState(true);

  // Fetch certificates from Firebase
  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        const certificatesRef = ref(db, 'certificates');
        const snapshot = await get(certificatesRef);
        if (snapshot.exists()) {
          const certificatesData = [];
          snapshot.forEach((childSnapshot) => {
            certificatesData.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setCertificates(certificatesData);
        } else {
          setCertificates([]);
        }
      } catch (error) {
        console.error('Error fetching certificates:', error);
        setCertificates([]);
      } finally {
        setCertificatesLoading(false);
      }
    };
    
    fetchCertificates();
  }, []);

  const skills = [
    {
      name: 'HTML',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg'
    },
    {
      name: 'CSS',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg'
    },
    {
      name: 'JavaScript',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg'
    },
    {
      name: 'Python',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg'
    },
    {
      name: 'Java',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg'
    },
    {
      name: 'React',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg'
    },
    {
      name: 'Node.js',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg'
    },
    {
      name: 'Git',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg'
    },
    {
      name: 'Figma',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg'
    },
    {
      name: 'MongoDB',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg'
    },
    {
      name: 'MySQL',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg'
    },
    {
      name: 'Kotlin',
      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg'
    }
  ];

  const allCategories = ['All', 'Cybersecurity', 'Networking', 'ML', 'Frontend', 'Mobile'];
  
  // Filter categories in hacker theme to show only All, Cybersecurity, and Networking
  const categories = isHackerTheme 
    ? ['All', 'Cybersecurity', 'Networking']
    : allCategories;
  
  // Filter certificates based on category
  let filteredCertificates = selectedCategory === 'All' 
    ? certificates 
    : certificates.filter(cert => cert.category === selectedCategory);

  // In hacker theme, only show Cybersecurity and Networking certificates
  if (isHackerTheme) {
    filteredCertificates = filteredCertificates.filter(cert => 
      cert.category === 'Cybersecurity' || cert.category === 'Networking'
    );
  }

  const handleDownloadDegree = () => {
    // Add your degree certificate download logic here
    console.log('Downloading Degree Certificate...');
  };

  const handleDownloadTranscript = () => {
    // Add your transcript download logic here
    console.log('Downloading Transcript...');
  };

  const renderEducation = () => (
    <div className="tab-content">
      <div className="education-content">
        <div className="education-details">
          <p><strong>School:</strong> St Marys College Kegalle</p>
          <p><strong>University:</strong> General Sir John Kotelawala Defence University Sri Lanka</p>
          <p><strong>Degree:</strong> BSc (Hons) Computer Science Degree</p>
          {/*<p><strong>GPA:</strong> 3.8/4.0</p>*/}
          
          {/* Education Document Buttons */}
          <div className="education-buttons">
            <button className="education-btn degree-btn" onClick={handleDownloadDegree}>
              Degree Certificate
            </button>
            <button className="education-btn transcript-btn" onClick={handleDownloadTranscript}>
              Transcript
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSkills = () => (
    <div className="tab-content">
      <div className="skills-content">
        <div className="skills-grid">
          {skills.map((skill, index) => (
            <div key={index} className="skill-item">
              <div className="skill-logo-container">
                <img src={skill.logo} alt={skill.name} className="skill-logo" />
              </div>
              <span className="skill-name">{skill.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Function to generate PDF preview image URL
  const getPdfPreviewUrl = (pdfUrl) => {
    // Extract the public ID from the PDF URL
    const urlParts = pdfUrl.split('/');
    const uploadIndex = urlParts.indexOf('upload');
    const publicIdWithExtension = urlParts.slice(uploadIndex + 2).join('/');
    const publicId = publicIdWithExtension.replace('.pdf', '');
    
    // Generate preview URL with Cloudinary transformations
    return `https://res.cloudinary.com/dksnd2kit/image/upload/w_400,h_300,c_fill,f_jpg,pg_1/${publicId}.jpg`;
  };

  const renderCertificates = () => (
    <div className="tab-content">
      <div className="certificates-content">
        <div className="categoryButtons">
          {categories.map((cat, index) => (
            <button
              key={index}
              className={`categoryBtn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
        {certificatesLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', fontSize: '1.1rem', color: '#666' }}>
            Loading certificates...
          </div>
        ) : (
          <div className="certificatesList">
            {filteredCertificates.map((item, i) => (
              <div key={item.id || i} className="certificateWrapper">
                <a
                  href={item.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer" 
                  className="certificateBox"
                >
                  <img 
                    src={getPdfPreviewUrl(item.pdfUrl)} 
                    alt={item.title} 
                    className="certificateImage" 
                  />
                  <div className="certificateContent" data-category={item.category}>
                  </div>
                </a>
                <div className="certificateInfo">
                  <h3 className="certificateTitle">{item.title}</h3>
                  <h5 className="certificateIssuer">Issued by: {item.issuer}</h5>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`tabbed-info-card ${isHackerTheme ? 'hacker-theme' : ''}`}>
      <div className="tab-header">
        <button 
          className={`tab-btn ${activeTab === 'education' ? 'active' : ''}`}
          onClick={() => setActiveTab('education')}
        >
          Education
        </button>
        <button 
          className={`tab-btn ${activeTab === 'skills' ? 'active' : ''}`}
          onClick={() => setActiveTab('skills')}
        >
          Skills
        </button>
        <button 
          className={`tab-btn ${activeTab === 'certificates' ? 'active' : ''}`}
          onClick={() => setActiveTab('certificates')}
        >
          Certifications
        </button>
      </div>
      
      <div className="tab-body">
        {activeTab === 'education' && renderEducation()}
        {activeTab === 'skills' && renderSkills()}
        {activeTab === 'certificates' && renderCertificates()}
      </div>
    </div>
  );
};

export default TabbedInfoCard;